import Link from "next/link";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { V3ButtonColor, V3ButtonVariant } from "../Button/V3Button.types";
import { V3IconPlay, V3IconPodcast } from "../Icons";
import { V3EmptyStateBtn } from "../ReusableStyledComponents/EmptyStateButton";
import { UnstyledLink } from "../ReusableStyledComponents/UnstyledLink";
import { MPUButtons, MPUEmptyState } from "./V3MediaPlaybackUnavailable.styles";

export enum MediaPlaybackUnavailableVariant {
    Visitor = "visitor",
    OwnerUpgrade = "ownerUpgrade",
    OwnerContact = "ownerContact"
}

interface V3MediaPlaybackUnavailableProps {
    variant: MediaPlaybackUnavailableVariant;
    audio?: boolean;
}

const PLAYBACK_LIMITS_HELP_URL = "https://authory.com/help/en/articles/17885087-how-do-playback-limits-for-audio-and-video-work";

const ALLOWANCE_TEXT =
    "Your plan includes a fair-use allowance for playing audio and video files you upload. You've used it up, so playback is paused until the start of next month. Your file is safe and nothing has been deleted.";

/**
 * Stacked media placeholder icon in the style of the other empty states.
 * @param audio Shows a waveform instead of a play glyph
 * @returns The icon svg
 */
function MediaPlaceholderIcon({ audio }: { audio: boolean }) {
    return (
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="13" width="40" height="40" rx="8" fill="#DDDFE3" />
            <rect x="11" y="21" width="46" height="46" rx="9" fill="#F4F4F6" stroke="#ABB0BA" strokeWidth="2" strokeDasharray="6 6" />
            {audio ? (
                <V3IconPodcast x={24} y={34} width={20} height={20} stroke="#ABB0BA" />
            ) : (
                <V3IconPlay x={22.75} y={34} width={20} height={20} color="#ABB0BA" />
            )}
        </svg>
    );
}

/**
 * Shown in place of an uploaded audio or video file that cannot be played.
 * @param variant Visitors get a neutral message, owners learn about the playback limit and what to do
 * @param audio Whether the file is audio
 * @returns The empty state box
 */
export function V3MediaPlaybackUnavailable({ variant, audio = false }: V3MediaPlaybackUnavailableProps) {
    const mediaLabel = audio ? "audio" : "video";
    const icon = <MediaPlaceholderIcon audio={audio} />;

    if (variant === MediaPlaybackUnavailableVariant.Visitor) {
        return (
            <MPUEmptyState
                title={`This ${mediaLabel} can't be played right now`}
                description="Please check back later."
                svgIcon={icon}
                maxWidth={640}
                customPadding="48px 0"
                skipExtendMobile
            />
        );
    }

    const isUpgrade = variant === MediaPlaybackUnavailableVariant.OwnerUpgrade;

    return (
        <MPUEmptyState
            title={`This ${mediaLabel} has reached its playback limit`}
            description={`${ALLOWANCE_TEXT} ${
                isUpgrade
                    ? "To keep it playing now, upgrade to the Professional plan."
                    : "Need it playing sooner? Write to us and we'll look at your account."
            }`}
            svgIcon={icon}
            maxWidth={640}
            customPadding="48px 0"
            skipExtendMobile
        >
            <MPUButtons>
                {isUpgrade ? (
                    <UnstyledLink as={Link} href={`${APPLICATION_ROUTES.SETTINGS_BILLING}?update=plan`}>
                        <V3EmptyStateBtn text="Upgrade to Professional" autoWidth variant={V3ButtonVariant.large} />
                    </UnstyledLink>
                ) : (
                    <UnstyledLink href="mailto:hello@authory.com?subject=Playback%20limit">
                        <V3EmptyStateBtn text="Contact us" autoWidth variant={V3ButtonVariant.large} />
                    </UnstyledLink>
                )}
                <UnstyledLink href={PLAYBACK_LIMITS_HELP_URL} target="_blank" rel="noopener noreferrer">
                    <V3EmptyStateBtn text="How playback limits work" autoWidth variant={V3ButtonVariant.large} color={V3ButtonColor.secondary} />
                </UnstyledLink>
            </MPUButtons>
        </MPUEmptyState>
    );
}
